import { useState } from "react";
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Snackbar,
  TextField, Typography,
} from "@mui/material";
import { api, errorMessage } from "../services/api";
import { useApiData } from "../hooks/useApiData";
import { useSession } from "../context/SessionContext";
import { DataState } from "../components/Feedback";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import DataTable, { TwoLine } from "../components/DataTable";
import { formatDate } from "../utils/format";

// Staff roles see every department; the others belong to one department.
const ROLES = [
  { value: "PlatformAdmin", label: "Platform admin", staff: true, help: "Everything, all departments, and approving people." },
  { value: "GRCManager", label: "GRC manager", staff: true, help: "Manages and approves, all departments." },
  { value: "RiskAnalyst", label: "Risk analyst", staff: true, help: "Reviews questionnaires and edits, all departments." },
  { value: "ClientAdmin", label: "Department admin", staff: false, help: "Edits and approves in one department." },
  { value: "ControlOwner", label: "Application owner", staff: false, help: "Answers questionnaires in one department; otherwise read-only." },
  { value: "Auditor", label: "Auditor", staff: false, help: "Read-only in one department." },
  { value: "ClientViewer", label: "Viewer", staff: false, help: "Read-only in one department." },
];
const roleLabel = (value) => (ROLES.find((r) => r.value === value) || {}).label || value;

function AccessDialog({ user, departments, onClose, onSaved, onError }) {
  const [role, setRole] = useState(user.role || "");
  const [departmentId, setDepartmentId] = useState(user.departmentId ? String(user.departmentId) : "");
  const [saving, setSaving] = useState(false);

  const chosen = ROLES.find((r) => r.value === role);
  const needsDepartment = chosen && !chosen.staff;
  const ready = chosen && (!needsDepartment || departmentId);

  async function save() {
    setSaving(true);
    try {
      await api.setUserAccess(user.username, { role, departmentId: needsDepartment ? Number(departmentId) : null });
      onSaved(user.pending ? `Approved ${user.email} as ${roleLabel(role)}.` : `Updated ${user.email}.`);
    } catch (e) {
      onError(errorMessage(e));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{user.pending ? "Approve" : "Change access for"} {user.email}</DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: "8px !important" }}>
        <TextField select size="small" label="Role" value={role} onChange={(e) => setRole(e.target.value)}
          helperText={chosen ? chosen.help : "Pick what this person may do."}>
          {ROLES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
        </TextField>
        {needsDepartment && (
          <TextField select size="small" label="Department" value={departmentId}
            onChange={(e) => setDepartmentId(e.target.value)} helperText="They will only see this department.">
            {departments.map((d) => <MenuItem key={d.id} value={String(d.id)}>{d.name}</MenuItem>)}
          </TextField>
        )}
        {!user.pending && (
          <Typography variant="caption" color="text.secondary">
            The change applies the next time they sign in.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={!ready || saving}>
          {user.pending ? "Approve" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RemoveDialog({ user, onClose, onRemoved, onError }) {
  const [busy, setBusy] = useState(false);
  async function remove() {
    setBusy(true);
    try {
      await api.removeUser(user.username);
      onRemoved(`Removed ${user.email}.`);
    } catch (e) {
      onError(errorMessage(e));
      setBusy(false);
    }
  }
  return (
    <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>{user.pending ? "Reject" : "Remove"} {user.email}?</DialogTitle>
      <DialogContent>
        <Typography>
          This deletes their account. They can sign up again later and would need approval again.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={remove} disabled={busy}>
          {user.pending ? "Reject" : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UsersPage() {
  const { me } = useSession();
  const state = useApiData(() => api.getUsers(), []);
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [notice, setNotice] = useState(null); // { severity, text }

  const done = (text) => {
    setEditing(null);
    setRemoving(null);
    setNotice({ severity: "success", text });
    state.reload();
  };
  const failed = (text) => setNotice({ severity: "error", text });

  const columns = [
    {
      key: "email", label: "Person", sortValue: (u) => (u.email || "").toLowerCase(),
      render: (u) => (
        <TwoLine main={u.email} sub={u.name && u.name !== u.email ? u.name : u.self ? "You" : ""} />
      ),
    },
    {
      key: "role", label: "Role", sortValue: (u) => (u.pending ? "" : roleLabel(u.role)),
      render: (u) => u.pending
        ? <Chip size="small" color="warning" label="Waiting for approval" />
        : <Chip size="small" color="primary" variant="outlined" label={roleLabel(u.role)} />,
    },
    { key: "departmentName", label: "Department", sortValue: (u) => u.departmentName || "", render: (u) => u.departmentName || (u.pending ? "—" : "All departments") },
    { key: "status", label: "Email", sortValue: (u) => u.status, render: (u) => (u.status === "CONFIRMED" ? "Confirmed" : "Not confirmed") },
    { key: "createdAt", label: "Signed up", sortValue: (u) => u.createdAt || "", render: (u) => formatDate((u.createdAt || "").slice(0, 10)) },
    {
      key: "actions", label: "", sortable: false,
      render: (u) => u.self ? null : (
        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <Button size="small" variant={u.pending ? "contained" : "outlined"} onClick={() => setEditing(u)}>
            {u.pending ? "Approve" : "Change"}
          </Button>
          <Button size="small" color="error" onClick={() => setRemoving(u)}>{u.pending ? "Reject" : "Remove"}</Button>
        </Box>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Users & approvals"
        subtitle="People who sign up can't see anything until you approve them here and give them a role."
      />
      <DataState state={state}>
        {(rows) => {
          // The table adds its own Department column when rows have a "department", so use another name here.
          const people = rows.map((u) => ({ ...u, id: u.username, departmentName: u.department, department: undefined }));
          const waiting = people.filter((u) => u.pending).length;
          return (
            <>
              {waiting > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {waiting} {waiting === 1 ? "person is" : "people are"} waiting for approval.
                </Alert>
              )}
              <SectionCard>
                <DataTable columns={columns} rows={people} emptyText="No one has signed up yet." />
              </SectionCard>
            </>
          );
        }}
      </DataState>

      {editing && (
        <AccessDialog user={editing} departments={me.availableClients} onClose={() => setEditing(null)}
          onSaved={done} onError={failed} />
      )}
      {removing && <RemoveDialog user={removing} onClose={() => setRemoving(null)} onRemoved={done} onError={failed} />}
      <Snackbar open={Boolean(notice)} autoHideDuration={6000} onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {notice ? <Alert severity={notice.severity} variant="filled" onClose={() => setNotice(null)}>{notice.text}</Alert> : undefined}
      </Snackbar>
    </>
  );
}
